import { ITableHeader } from "components/Table";

export interface EmptyStateConfig {
  title: string;
  message: string;
}

export interface TableConfig {
  route: string;
  viewAllButtonText: string;
  headers: ITableHeader[];
  emptyState: EmptyStateConfig;
}

// Default empty state config
export const DEFAULT_EMPTY_STATE: EmptyStateConfig = {
  title: "No data available",
  message: "This table is currently empty.",
};

// Empty state for analytics pages with filters/date ranges
export const ANALYTICS_EMPTY_STATE: EmptyStateConfig = {
  title: "No data with your selected options",
  message:
    "Try adjusting your date range or filters, or start sending requests to see analytics and trends here.",
};

// Error table headers
const ERROR_TABLE_HEADERS: ITableHeader[] = [
  {
    key: "request_id",
    label: "ID",
    tooltip: "Unique ID to identify your error log",
  },
  {
    key: "timestamp",
    label: "Time",
    tooltip: "Specific date and time when your request failed",
  },
  {
    key: "severity",
    label: "Severity",
    tooltip:
      "Severity is automatically determined based on the error type to help you prioritize errors.",
  },
  {
    key: "url",
    label: "URL",
    tooltip: "URL that was sent in the failed request.",
  },
  {
    key: "status_code",
    label: "Status code",
    tooltip: "Error status why your request failed.",
  },
  {
    key: "retries",
    label: "Number of retries",
    tooltip:
      "We automatically retry failed requests. This shows how many attempts were made before the final status.",
  },
];

// table configs for analytics pages
export const TABLE_CONFIGS: Record<string, TableConfig> = {
  error: {
    route: "/analytics/error-logs",
    viewAllButtonText: "VIEW ALL ERRORS",
    headers: ERROR_TABLE_HEADERS,
    emptyState: {
      title: "No errors found",
      message:
        "Great news! No failed requests in this time period. Try adjusting your date range to see historical errors.",
    },
  },
  domain: {
    route: "/analytics/domain-analytics",
    viewAllButtonText: "VIEW ALL DOMAINS",
    headers: [
      { key: "domain", label: "Domain" },
      { key: "requests", label: "Requests" },
      {
        key: "cancelledRequests",
        label: "Cancelled Requests",
        tooltip:
          "Requests you stopped before they finished, for example by closing the connection. You don't get a response for these, but they're still charged.",
      },
      { key: "successRate", label: "Success Rate" },
      { key: "credits", label: "Credits" },
      { key: "ultraPremium", label: "Ultra Premium" },
      { key: "premium", label: "Premium" },
      { key: "render", label: "Render" },
      {
        key: "aiParser",
        label: "AI Parser",
        tooltip: "Total credits charged for scrapes on this domain that used an AI parser",
      },
    ],
    emptyState: {
      title: "No domain data found",
      message:
        "Try adjusting your date range or filters. Domain insights will appear once requests are made.",
    },
  },
};

export type TableType = keyof typeof TABLE_CONFIGS;
